import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import EditPastReimbursmentModalModal from 'adminDashboard/reimbursements/common/editPastReimbursmentModal'

import { PlanPeriodDto, getBenefitDetailsForActivePlanPeriod } from 'core'
import getReimbursementsHistory, {
  PagedReimbursementsHistory,
  ReimbursementHistoryFilter,
  ReimbursementHistoryItem,
  getReimbursementsHistoryAsCsv,
} from 'core/application/adminDashboard/reimbursements/reimbursementsHistory/getReimbursementsHistory'

import { queryRequest } from 'common/RequestUtils'
import { Button, Col, RangePickerV2, Row, Search, Table } from 'common/components'
import { now, toDateWithoutTimeZone } from 'common/dateFormatters'
import { downloadCSV } from 'common/downloadFile'
import { useQueryRequest, useQueryRequestV2 } from 'common/hooks'

import { reimbursementsHistoryColumns } from './ReimbursementsHistoryColumns'

import './ReimbursementsHistory.less'

const pagedColumnDefaultValues = {
  items: [],
  pageCount: 1,
  pageNumber: 1,
  pageSize: 10,
  totalItemCount: 0,
}

interface ReimbursementsHistoryProps {
  planPeriod: PlanPeriodDto
}

const ReimbursementsHistory = ({ planPeriod }: ReimbursementsHistoryProps) => {
  const [editReimbursmentModalOpen, setEditReimbursmentModalOpen] = useState<boolean>(true)
  const [selectedUser, setSelectedUser] = useState<ReimbursementHistoryItem>(null)
  const {
    data: reimbursementsHistory,
    isLoading: loadingHistory,
    sendQuery: sendGetPagedHistory,
  } = useQueryRequestV2<PagedReimbursementsHistory>({
    initialState: pagedColumnDefaultValues,
  })
  const [filter, setFilter] = useState<ReimbursementHistoryFilter>({
    searchValue: '',
    fromDate: null,
    toDate: null,
    pageNumber: 1,
    pageSize: 10,
    orderColumn: 'scheduleDate',
    orderDescending: true,
  })

  const [planPeriodDetails] = useQueryRequest(null, () => getBenefitDetailsForActivePlanPeriod())
  const hasESPR = planPeriodDetails && planPeriodDetails.productClasses.some((x) => x.reimburseAnotherEmployeePlan)

  const { t: translate } = useTranslation()

  const handleModal = (user: ReimbursementHistoryItem) => {
    setEditReimbursmentModalOpen(true)
    setSelectedUser(user)
  }

  const handleCloseModal = () => {
    setEditReimbursmentModalOpen(false)
  }

  const onReimbursementsHistoryTableChange = (pagination: any, filters: any, sorter: any) => {
    setFilter({
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: sorter.field || 'scheduleDate',
      orderDescending: sorter.order !== 'desc',
      fromDate: dateRangeValues.fromDate,
      toDate: dateRangeValues.toDate,
    })
  }
  const planPeriodEndDate = toDateWithoutTimeZone(planPeriod.endDate)
  const today = now()

  const [dateRangeValues, setDateRangeValues] = useState<{ fromDate: string; toDate: string }>({
    fromDate: null,
    toDate: null,
  })
  useEffect(() => {
    setFilter((filter: ReimbursementHistoryFilter) => ({
      ...filter,
      fromDate: dateRangeValues.fromDate,
      toDate: dateRangeValues.toDate,
    }))

    setDateRangeValues({
      fromDate: dateRangeValues.fromDate,
      toDate: dateRangeValues.toDate,
    })
  }, [planPeriod])

  useEffect(() => {
    ;(async () => {
      await sendGetPagedHistory(() =>
        getReimbursementsHistory(planPeriod.id, {
          ...filter,
          fromDate: dateRangeValues.fromDate,
          toDate: dateRangeValues.toDate,
        }),
      )
    })()
  }, [filter])

  const onSearch = (searchValue: string) => {
    setFilter({
      ...filter,
      searchValue: searchValue,
      pageNumber: 1,
    })
  }

  const handleDateChange = async (values: any) => {
    const fromDateValue = values ? values[0] : null
    const toDateValue = values ? values[1] : null

    await sendGetPagedHistory(() => {
      setDateRangeValues({
        fromDate: fromDateValue,
        toDate: toDateValue,
      })
      return getReimbursementsHistory(planPeriod.id, {
        ...filter,
        fromDate: fromDateValue,
        toDate: toDateValue,
        pageNumber: 1,
      })
    })
    setFilter({
      ...filter,
      fromDate: fromDateValue,
      toDate: toDateValue,
      pageNumber: 1,
    })
  }

  const downloadCsvHandler = async () => {
    var result = await queryRequest(() =>
      getReimbursementsHistoryAsCsv(planPeriod.id, '', {
        ...filter,
        fromDate: dateRangeValues.fromDate,
        toDate: dateRangeValues.toDate,
      }),
    )
    downloadCSV(result, 'Reimbursements_history.csv')
  }

  return (
    <div className="reimbursements_history">
      <Row align={'middle'} justify={'space-between'}>
        <Col span={18}>
          <Row align={'middle'} gutter={10}>
            <Col xs={24} sm={24} md={8} lg={8} xl={8} className="mb-8">
              <Search onSearch={onSearch} allowClear enterButton />
            </Col>
            <Col xs={24} sm={24} md={16} lg={16} xl={16} className="mb-8">
              <Row align="middle" gutter={6} justify={'center'}>
                <Col>
                  <label>Date Scheduled</label>
                </Col>
                <Col>
                  <RangePickerV2 allowClear className={'w-100'} format="MM/DD/YYYY" onChange={handleDateChange} />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col flex={'auto'} className="pb-16">
          <Button
            type="primary"
            htmlType="button"
            icon="far fa-download"
            className="download_csv"
            disabled={reimbursementsHistory.items.length === 0}
            onClick={downloadCsvHandler}
          >
            {translate('admin-dashboard.reimbursements.reimbursements-history.download-csv-label')}
          </Button>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Table
            className={'t-reimbursement-history'}
            dataSource={reimbursementsHistory?.items}
            onChange={onReimbursementsHistoryTableChange}
            loading={loadingHistory}
            scroll={{ x: 1000 }}
            columns={reimbursementsHistoryColumns(translate, filter, handleModal, hasESPR)}
            pagination={{
              total: reimbursementsHistory.totalItemCount,
              pageSize: reimbursementsHistory.pageSize,
              current: reimbursementsHistory.pageNumber,
            }}
            rowKey={(record) => `${record.fullName}-${record.scheduleDate}`}
          />
        </Col>
      </Row>
      {editReimbursmentModalOpen && selectedUser && (
        <EditPastReimbursmentModalModal
          isOpen={editReimbursmentModalOpen}
          onClose={handleCloseModal}
          onSubmit={async () => {
            await sendGetPagedHistory(() => getReimbursementsHistory(planPeriod.id, filter))
            handleCloseModal()
          }}
          selectedUser={selectedUser}
          planPeriodId={planPeriod.id}
        />
      )}
    </div>
  )
}

export default ReimbursementsHistory
